import type { JSX, ReactNode } from 'react';
import { createContext, useContext, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { API_CONFIGURED } from './apiBase';
import { ApiPlanDataClient } from './api/ApiPlanDataClient';
import type { IPlanDataClient } from './planDataClient';
import { FixturePlanDataClient } from './fixture/FixturePlanDataClient';

const PlanDataContext = createContext<IPlanDataClient | null>(null);

/**
 * Chooses the app's data seam — the exact mirror of HouseholdDataProvider.
 *
 * SECURITY (hard-rules #1–4): client data must only ever be persisted through
 * the RLS-protected API, never the browser.
 *
 * - A configured API (`VITE_API_URL`) → `ApiPlanDataClient`, sending the auth
 *   session's bearer token so every read/write is RLS-scoped. The real path for
 *   local API dev, staging and production.
 * - No API URL + dev build → the in-memory `FixturePlanDataClient` (seeded
 *   demo), a DEV/DEMO convenience only.
 * - No API URL + production build → `ApiPlanDataClient` with no token, which
 *   fails closed rather than silently serving fixture data as if it were real.
 */
function defaultPlanClient(getToken: () => string | null): IPlanDataClient {
  if (API_CONFIGURED) return new ApiPlanDataClient(getToken);
  if (import.meta.env.DEV) return new FixturePlanDataClient();
  return new ApiPlanDataClient(() => null);
}

export function PlanDataProvider({
  client,
  children,
}: {
  client?: IPlanDataClient;
  children: ReactNode;
}): JSX.Element {
  const { getToken } = useAuth();
  const ref = useRef<IPlanDataClient>(client ?? defaultPlanClient(getToken));
  return <PlanDataContext.Provider value={ref.current}>{children}</PlanDataContext.Provider>;
}

export function usePlanData(): IPlanDataClient {
  const ctx = useContext(PlanDataContext);
  if (ctx === null) {
    throw new Error('usePlanData must be used inside PlanDataProvider');
  }
  return ctx;
}
